import { CandidateProfile, CandidateStory, TheDMs, Opportunity, TheProof, User } from "../types";
import { createId, nowIso } from "../workflow";
import { EMPTY_SIGNALS, mergeSignalSets } from "./signals";

function bulletOrFallback(values: string[], fallback: string): string {
  return values.length ? values.join(", ") : fallback;
}

function buildEvidenceLinkedClaims(artifacts: TheProof[]) {
  return artifacts.slice(0, 5).flatMap((artifact) => {
    const text = artifact.content_summary || artifact.evidence_note || artifact.source_label;
    return text
      ? [
          {
            text,
            citation_proof_id: artifact.proof_id,
            is_verified: true,
          },
        ]
      : [];
  });
}

export function generateCandidateStory(input: {
  user?: User;
  opportunity: Opportunity;
  profile?: CandidateProfile;
  artifacts: TheProof[];
  correspondence: TheDMs[];
}): Omit<CandidateStory, "story_id" | "updated_at"> {
  const aggregatedSignals = mergeSignalSets([
    ...input.artifacts.map((artifact) => artifact.extracted_signals ?? EMPTY_SIGNALS),
    ...input.correspondence.map((item) => item.extracted_signals ?? EMPTY_SIGNALS),
  ]);

  const title = `${input.user?.full_name || "Candidate"} story for ${input.opportunity.company_name}`;
  const summary = `${input.opportunity.role_title} narrative built from lifecycle evidence, structured signals, and profile context.`;
  const who =
    input.profile?.experience_level || input.user?.current_role || "a professional in transition";
  const what =
    input.profile?.skills_summary || "translating prior experience into a clearer next-step story";
  const why = input.opportunity.opportunity_source
    ? `This opportunity matters because it connects through ${input.opportunity.opportunity_source} and aligns to the target role of ${input.opportunity.role_title}.`
    : `This opportunity matters because it is a concrete path toward ${input.opportunity.role_title}.`;
  const where = bulletOrFallback(
    aggregatedSignals.locations.length
      ? aggregatedSignals.locations
      : [input.opportunity.location_type],
    "the settings where this opportunity is expected to operate",
  );
  const when = bulletOrFallback(
    [...aggregatedSignals.dates, ...aggregatedSignals.times],
    "upcoming milestones are still being clarified",
  );
  const how =
    input.profile?.domain_strengths ||
    "through evidence-backed communication, practical delivery, and steady follow-through";
  const gaps = input.profile?.declared_gaps || "No major gaps have been declared yet.";
  const proofPoints = bulletOrFallback(
    aggregatedSignals.companies.length
      ? aggregatedSignals.companies.map((company) => `Experience or signal linked to ${company}`)
      : input.artifacts.slice(0, 3).map((artifact) => artifact.source_label),
    "More proof points should be added through proof, DMs, and debrief notes.",
  );
  const interviewCues = bulletOrFallback(
    aggregatedSignals.interviews,
    "No interview-specific signals captured yet.",
  );

  const markdown = `# ${title}

## Story Summary
${summary}

## Who
I am ${who}. My current narrative shows that I bring ${what}.

## What
I am pursuing the ${input.opportunity.role_title} opportunity at ${input.opportunity.company_name} with a focus on clear, supportable evidence rather than generic claims.

## Why
${why}

## Where
I create value in ${where}.

## When
The current timeline signals include ${when}.

## How
I communicate and execute by leaning on ${how}.

## Proof Points
${proofPoints}

## Gaps To Address
${gaps}

## Interview And Outreach Cues
${interviewCues}

## Structured Signals
- Names: ${bulletOrFallback(aggregatedSignals.names, "None captured")}
- Emails: ${bulletOrFallback(aggregatedSignals.emails, "None captured")}
- Phones: ${bulletOrFallback(aggregatedSignals.phones, "None captured")}
- Companies: ${bulletOrFallback(aggregatedSignals.companies, "None captured")}
- Locations: ${bulletOrFallback(aggregatedSignals.locations, "None captured")}
- Contingencies: ${bulletOrFallback(aggregatedSignals.contingencies, "None captured")}
`;

  return {
    opportunity_id: input.opportunity.opportunity_id,
    title,
    summary,
    markdown,
    status: "review",
    source_proof_ids: input.artifacts.map((artifact) => artifact.proof_id),
    source_dm_ids: input.correspondence.map((item) => item.dm_id),
    source_artifact_ids: input.artifacts.map((artifact) => artifact.proof_id),
    source_correspondence_ids: input.correspondence.map((item) => item.dm_id),
    claims: buildEvidenceLinkedClaims(input.artifacts),
  };
}

export function createCandidateStoryRecord(
  story: Omit<CandidateStory, "story_id" | "updated_at">,
): CandidateStory {
  return {
    ...story,
    story_id: createId("story"),
    updated_at: nowIso(),
    claims: (story as any).claims || [],
  };
}
